type AnyObject = { [prop: string]: any };

export function getEntryBy<T extends AnyObject>(
  uniqueValue: string,
  elements: T[],
  uniqueValueKey: string = 'uid'
): T | undefined {
  return elements.find((entry) => entry[uniqueValueKey] === uniqueValue);
}

export function getIndexBy<T extends AnyObject>(
  uniqueValue: string,
  elements: T[],
  uniqueValueKey: string = 'uid'
): number {
  return elements.findIndex((entry) => entry?.[uniqueValueKey] === uniqueValue);
}

export function modifyElement<T extends AnyObject, KEY extends keyof T = 'uid'>(
  elements: T[] | null,
  propertyNameToReplace: KEY,
  uniqueValueKey: string = 'uid'
): (uniqueValues: string[], targetValue: T[KEY]) => T[] {
  return (uids, targetValue) => modifyProperty(uids, propertyNameToReplace, targetValue, elements, uniqueValueKey);
}

export function modifyProperty<T extends AnyObject, KEY extends keyof T = 'uid'>(
  uniqueValues: string[],
  propertyNameToReplace: KEY,
  targetValue: T[KEY],
  elements: T[] | null,
  uniqueValueKey: string = 'uid'
): T[] {
  if (!elements) {
    return [];
  }
  const data = structuredClone(elements);
  uniqueValues.forEach((uid) => {
    const entry = getEntryBy(uid, data, uniqueValueKey);
    if (entry) {
      entry[propertyNameToReplace] = targetValue;
    }
  });
  return [...data];
}

export function removeElements<T extends AnyObject>(
  uniqueValues: string[],
  entries: T[],
  uniqueValueKey: string = 'uid'
): T[] {
  const result: T[] = structuredClone(entries);
  uniqueValues.forEach((uid) => {
    result.splice(getIndexBy(uid, entries, uniqueValueKey), 1);
  });
  return result;
}

export function replaceElement<T extends { [prop: string]: any }>(
  model: T,
  entries: T[],
  uniqueValueKey: string = 'uid'
): T[] {
  const result: T[] = structuredClone(entries);
  if (isPropertyOf(model, uniqueValueKey)) {
    const index = getIndexBy(model.key, entries, uniqueValueKey);
    if (index !== -1) {
      result.splice(index, 1, structuredClone(model));
    }
  } else {
    throw new Error(`No "${uniqueValueKey}" property in object: ${JSON.stringify(model)}`);
  }
  return result;
}

function isPropertyOf<T extends Record<string, any>, K extends keyof T>(
  model: T,
  uniqueValueKey: any
): uniqueValueKey is K {
  // @ts-ignore: Provide better description why we use ts-ignore here
  return !!model[uniqueValueKey];
}

export function replaceElementByIndex<T>(model: T, entries: T[], index: number): T[] {
  const result: T[] = structuredClone(entries);
  if (index !== -1) {
    result.splice(index, 1, structuredClone(model));
  }
  return result;
}

interface FilterNotEmptyOptions {
  removeEmptyStrings?: boolean;
  removeEmptyArrays?: boolean;
}
export function filterNotEmpty<MODEL>(
  values?: (MODEL | undefined | null | false)[],
  options?: FilterNotEmptyOptions
): MODEL[] {
  return (values ?? []).filter((value) => notEmpty(value, options)) as MODEL[];
}

function notEmpty<MODEL>(value: MODEL | null | undefined | false, options?: FilterNotEmptyOptions): value is MODEL {
  return !(
    value === null ||
    value === undefined ||
    value === false ||
    (options?.removeEmptyStrings && typeof value === 'string' && value.trim() === '') ||
    (options?.removeEmptyArrays && Array.isArray(value) && value?.length === 0)
  );
}
